<template>
  <Modal
    :isOpen="isOpen"
    @update:isOpen="$emit('update:isOpen', $event)"
    modalId="edit-container-details-modal"
    subtitle="Update container fields, then save."
    maxWidth="5xl"
    @close="onCancel"
  >
    <template #title>
      Edit Container #{{ String(modelForEdit.internalIndex).padStart(2, '0') }}-{{ modelForEdit.containerId }}
      <CopyButton :value="String(modelForEdit.containerId)" title="Copy container number" ariaLabel="Copy container number" />
    </template>
    <template #content>
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Size Type</label>
          <div class="tw-mt-1">
            <FilterableDropdown
              v-model="modelForEdit.sizeType"
              :options="allSizeTypeOptions"
              class="tw-w-full"
              :clearable="false"
              :components="{ OpenIndicator: null, Deselect: null }">
            </FilterableDropdown>
          </div>
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Seal Number</label>
          <input type="text"
            class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
            v-model="modelForEdit.sealNumber" />
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Cargo Pieces</label>
          <input type="text"
            class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
            v-model="modelForEdit.cargoPieces" />
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Weight</label>
          <input type="text"
            class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
            v-model="modelForEdit.weight" />
        </div>

        <div class="md:tw-col-span-2">
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Cargo Description</label>
          <input type="text"
            class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
            v-model="modelForEdit.cargoDescription" />
        </div>

        <div class="md:tw-col-span-2">
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Reference #</label>
          <input type="text"
            class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
            v-model="modelForEdit.referenceNumber" />
        </div>
        <div v-if="false">
          <label class="tw-block tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase">EIR In</label>
          <BaseDateTimePicker type="datetime" value-type="format" format="YYYY-MM-DD HH:mm"
            v-model="modelForEdit.eirIn" class="tw-mt-1" />
        </div>

        <div v-if="false">
          <label class="tw-block tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase">EIR Out</label>
          <BaseDateTimePicker type="datetime" value-type="format" format="YYYY-MM-DD HH:mm"
            v-model="modelForEdit.eirOut" class="tw-mt-1" />
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Terminal Appt</label>
          <BaseDateTimePicker 
            value-type="YYYY-MM-DD HH:mm" 
            date-format="MM/DD/YYYY" date-time-format="MM/DD/YYYY HH:mm"
            :allow-time-toggling="true"
            :show-days-shortcuts="true"
            controls-placement="popup"
            :minute-step="15"
            time-panel-placement="beside-calendar"
            v-model="modelForEdit.terminalAppointmentDate"
            v-model:has-time="modelForEdit.terminalAppointmentHasTime" class="tw-mt-1" />
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">{{ isExportOrder ? 'Pick-Up Appt' : 'Delivery Appt' }}</label>
          <BaseDateTimePicker 
            value-type="YYYY-MM-DD HH:mm" 
            date-format="MM/DD/YYYY" date-time-format="MM/DD/YYYY HH:mm"
            :allow-time-toggling="true"
            :show-days-shortcuts="true"
            controls-placement="popup"
            :minute-step="15"
            time-panel-placement="beside-calendar"
            v-model="modelForEdit.deliveryAppointmentDate"
            v-model:has-time="modelForEdit.deliveryAppointmentHasTime" class="tw-mt-1" />
        </div>

        <div>
          <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-slate-700">
            <input
              v-model="modelForEdit.isReturningToAnotherLocation"
              type="checkbox"
              class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
            />
            Return to Another Location?
          </label>
          <ContainerReturnLocationDropdown
            v-if="modelForEdit.isReturningToAnotherLocation"
            v-model="modelForEdit.returnLocation"
            placeholder="select customer or terminal..."
            class="tw-mt-1 tw-w-full" />
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Return Terminal Appt</label>
          <BaseDateTimePicker 
            value-type="YYYY-MM-DD HH:mm" 
            date-format="MM/DD/YYYY" date-time-format="MM/DD/YYYY HH:mm"
            :allow-time-toggling="true"
            :show-days-shortcuts="true"
            controls-placement="popup"
            :minute-step="15"
            time-panel-placement="beside-calendar"
            v-model="modelForEdit.returnTerminalAppointmentDate"
            v-model:has-time="modelForEdit.returnTerminalAppointmentHasTime" class="tw-mt-1" />
        </div>
      </div>

      <div class="tw-mt-6 tw-flex tw-items-center tw-justify-end tw-gap-2">
        <Button :isGreen="true" :enableLoading="isSaving" :disabled="isSaving" :isPrimary="true" @click="onSave" class="tw-p-0"
          icon="check" title="Save container details" aria-label="Save container details">Save</Button>
        <Button :isRed="true" @click="onCancel" :isSecondary="true" class="tw-p-0"
          title="Cancel edit container details" aria-label="Cancel edit container details">Cancel</Button>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';

import Modal from '@/components/core/Modal/Modal.vue';
import FilterableDropdown from '@/components/core/FilterableDropdown.vue';
import Button from '@/components/core/Button.vue';
import BaseDateTimePicker from '../../core/DateTime/BaseDateTimePicker.vue';
import CopyButton from '@/components/core/CopyButton.vue';
import ContainerReturnLocationDropdown from '@/components/Container/ContainerReturnLocationDropdown.vue';

import { containerSizes, containerTypesValues } from '@/utils/container';
import type { ContainerReturnLocation } from '@/utils/container';

export type EditContainerDetailsModel = {
  id: string;
  containerId: string;
  internalIndex: string;
  sizeType?: string;
  sealNumber?: string;
  cargoPieces?: string;
  cargoDescription?: string;
  weight?: string;
  referenceNumber?: string;
  eirIn?: string;
  eirOut?: string;
  terminalAppointmentDate?: string;
  terminalAppointmentHasTime?: boolean;
  deliveryAppointmentDate?: string;
  deliveryAppointmentHasTime?: boolean;
  // UI-only: unchecked means "returns to the pick-up terminal" and clears returnLocation on save
  isReturningToAnotherLocation?: boolean;
  returnLocation?: ContainerReturnLocation | null;
  returnTerminalAppointmentDate?: string;
  returnTerminalAppointmentHasTime?: boolean;
};

export default defineComponent({
  components: {
    Modal,
    FilterableDropdown,
    Button,
    BaseDateTimePicker,
    CopyButton,
    ContainerReturnLocationDropdown,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    model: {
      type: Object as PropType<EditContainerDetailsModel | null>,
      default: null,
    },
    isExportOrder: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:isOpen', 'cancel', 'save'],
  data() {
    return {
      internalModel: {
        id: '',
        containerId: '',
        internalIndex: '',
        sizeType: undefined,
        sealNumber: undefined,
        cargoPieces: undefined,
        cargoDescription: undefined,
        weight: undefined,
        referenceNumber: undefined,
        eirIn: undefined,
        eirOut: undefined,
        terminalAppointmentDate: undefined,
        terminalAppointmentHasTime: true,
        deliveryAppointmentDate: undefined,
        deliveryAppointmentHasTime: true,
        isReturningToAnotherLocation: false,
        returnLocation: null,
        returnTerminalAppointmentDate: undefined,
        returnTerminalAppointmentHasTime: true,
      } as EditContainerDetailsModel,
    };
  },
  watch: {
    model: {
      immediate: true,
      handler(next) {
        if (!next) return;
        (this as any).internalModel = { ...next };
      },
    },
  },
  computed: {
    modelForEdit(): EditContainerDetailsModel {
      return (this as any).internalModel;
    },
    allSizeTypeOptions(): string[] {
      const options: string[] = [];
      containerSizes.forEach(size => {
        containerTypesValues.forEach(type => {
          options.push(`${size}${type}`);
        });
      });
      return options;
    },
  },
  methods: {
    onCancel() {
      (this as any).$emit('cancel');
    },
    onSave() {
      (this as any).$emit('save', (this as any).internalModel);
    },
  },
});
</script>
